import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Target, Headphones, Calendar, BookOpen, Star as StarIcon, Sparkles, CheckCircle2 } from "lucide-react";
import GlowEffect from "../common/GlowEffect";
import { useAIAgentData } from "../../hooks/useAIAgentData";

// Icon mapping dictionary to resolve string names to Lucide icon components
const iconMap: Record<string, React.ComponentType<any>> = {
  Target,
  Headphones,
  Calendar,
  BookOpen,
  Star: StarIcon,
  Sparkles,
};

function renderIcon(name: string, size: number, className?: string) {
  const Icon = iconMap[name];
  return Icon ? <Icon size={size} className={className} /> : null;
}

// Shared animation variants
const fadeUp = {
  hidden: { opacity: 0, y: 50 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, delay: i * 0.12 },
  }),
} as const;

export default function UseCasesSection() {
  const [activeTab, setActiveTab] = useState(0);
  const { data } = useAIAgentData();
  const { badgeText, title, useCases, industriesTitle, industriesSubtitle, industries } = data.useCasesSection;

  const active = useCases[activeTab];

  return (
    <section className="relative px-5 sm:px-10 md:px-16 sm:py-16 py-8">
      <GlowEffect direction="left" />
      <div className="space-y-24">
        {/* ── Tab Use-Case Layout ── */}
        <div className="space-y-12">
          <div className="text-center space-y-4">
            <motion.p variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}
              className="text-[#A6A0F6] text-sm font-medium tracking-widest uppercase">{badgeText}</motion.p>
            <motion.h2 variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={1}
              className="text-4xl md:text-6xl font-bold text-white">{title}</motion.h2>
          </div>

          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}
            className="grid lg:grid-cols-[280px_1fr] gap-0 rounded-2xl border border-white/10 overflow-hidden">

            {/* Tab list */}
            <div className="border-r border-white/10 bg-white/[0.02]">
              {useCases.map((uc, i) => (
                <button
                  key={i}
                  onClick={() => setActiveTab(i)}
                  className={`w-full flex items-center gap-4 px-6 py-5 text-left transition-all duration-200 border-b border-white/[0.06] last:border-b-0 ${
                    activeTab === i
                      ? "bg-[#1179F9]/15 border-l-2 border-l-[#1179F9]"
                      : "hover:bg-white/[0.04] border-l-2 border-l-transparent"
                  }`}
                >
                  <div
                    className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 transition-colors"
                    style={{
                      background: activeTab === i ? `${uc.color}30` : "rgba(255,255,255,0.06)",
                      color: activeTab === i ? uc.color : "#888",
                    }}
                  >
                    {renderIcon(uc.iconName, 26)}
                  </div>
                  <span className={`text-base font-medium transition-colors ${activeTab === i ? "text-white" : "text-[#999]"}`}>
                    {uc.title}
                  </span>
                </button>
              ))}
            </div>

            {/* Active detail panel */}
            <AnimatePresence mode="wait">
              <motion.div
                key={activeTab}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -10 }}
                transition={{ duration: 0.3 }}
                className="p-10 space-y-6 flex flex-col justify-center"
              >
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-2xl flex items-center justify-center" style={{ background: `${active.color}25`, color: active.color }}>
                    {renderIcon(active.iconName, 26)}
                  </div>
                  <div>
                    <p className="text-base font-medium tracking-wide" style={{ color: active.color }}>{active.tagline}</p>
                    <h3 className="text-2xl md:text-3xl font-bold text-white">{active.title}</h3>
                  </div>
                </div>

                <p className="text-2xl text-[#d9d9d9] leading-relaxed">{active.desc}</p>

                <ul className="space-y-3">
                  {active.bullets.map((b, i) => (
                    <li key={i} className="flex items-center gap-3">
                      <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: `${active.color}25` }}>
                        <CheckCircle2 size={13} style={{ color: active.color }} />
                      </div>
                      <span className="text-[#d9d9d9] text-lg">{b}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            </AnimatePresence>
          </motion.div>
        </div>

        {/* ── Industry chips ── */}
        <div className="space-y-10">
          <div className="text-center space-y-3">
            <motion.p variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}
              className="text-[#A6A0F6] text-sm font-medium tracking-widest uppercase">{industriesTitle}</motion.p>
            <motion.h2 variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={1}
              className="text-4xl md:text-5xl font-bold text-white">{industriesSubtitle}</motion.h2>
          </div>
          <div className="flex flex-wrap justify-center gap-5">
            {industries.map((ind, i) => (
              <motion.div key={i} variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={i}
                className="flex-1 min-w-[200px] max-w-[240px] p-6 rounded-2xl border border-white/10 bg-white/[0.03] space-y-2 text-center hover:border-[#1179F9]/30 transition-all">
                <h4 className="text-xl text-white font-semibold">{ind.name}</h4>
                <p className="text-base text-[#d9d9d9] leading-relaxed">{ind.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
